import { useRef, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useAuth from '@/hooks/useAuth';
import useInput from '@/hooks/useInput';
import useToggle from '@/hooks/useToggle';
import useRefreshToken from '@/hooks/useRefreshToken';
import { useGetCurrent } from '@/hooks/useGetCurrent';
import { useActivationGuard } from '@/hooks/useActivationGuard';
import { axiosPrivate } from '@/api/axios';
import { USER_API_PATH } from '@/lib/constants';
import { Card } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const Login = () => {
  const { logIn, setCurrentUser } = useAuth();
  const refresh = useRefreshToken();
  const getCurrent = useGetCurrent();
  const checkActivation = useActivationGuard();
  const navigate = useNavigate();
  const location = useLocation();

  const userRef: any = useRef();
  const errRef: any = useRef();

  const [user, resetUser, userAttribs] = useInput('user', '');
  const [password, setPassword] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [check, toggleCheck] = useToggle('persist', false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMaxSessions, setShowMaxSessions] = useState(false);

  useEffect(() => {
    userRef.current.focus();
  }, []);

  useEffect(() => {
    setErrMsg('');
  }, [user, password]);

  const loginWithSessionMode = async (sessionCloseMode?: 'closeOldest' | 'closeAll') => {
    try {
      setIsSubmitting(true);

      const data = await logIn(user, password, sessionCloseMode);

      // Refresh token to establish cookie session
      await refresh();

      // Load full user profile + run activation guard
      try {
        const fullUser = await getCurrent();
        setCurrentUser(fullUser);

        const activation = checkActivation(fullUser);

        // Patch lastLogin if first time
        if (activation.shouldPatchLastLogin) {
          try {
            await axiosPrivate.patch(`/${USER_API_PATH}/${fullUser.id}`, {
              lastLogin: new Date().toISOString(),
            });
          } catch {
            // Non-critical
          }
        }

        if (activation.shouldLogout) {
          setErrMsg(activation.message || 'Tu cuenta está inactiva.');
          return;
        }

        if (activation.redirect) {
          resetUser();
          setPassword('');
          navigate(activation.redirect, { replace: true });
          return;
        }
      } catch (err) {
        console.error('[Login getCurrent error]', err);
        // Fall through to default navigation if getCurrent fails
      }

      // Default navigation (no activation redirect)
      const sessionSpecificDefaultUrl = data.principalType === 'Company'
        ? '/shop/reports/eod'
        : data.principalType === 'user'
          ? '/admin/companies'
          : '/customer';

      const from = location.state?.from?.pathname || sessionSpecificDefaultUrl;
      resetUser();
      setPassword('');

      if (data.principalType === 'user' || data.principalType === 'Company' || data.principalType === 'CustomerApp') {
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      console.log('[login err]', err);

      if (!err?.data) {
        setErrMsg('Sin respuesta del servidor');
      } else if (err.data?.httpStatus === 400) {
        setErrMsg('Falta email o contraseña');
      } else if (err.data?.httpStatus === 401) {
        setErrMsg('Contraseña incorrecta');
      } else if (err.data?.httpStatus === 409) {
        setShowMaxSessions(true);
      } else {
        setErrMsg('Falló la sesión');
      }

      errRef.current?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    await loginWithSessionMode();
  };

  return (
    <div className="container mx-auto px-4 h-full">
      <div className="flex content-center items-center justify-center h-full">
        <div className="w-full lg:w-4/12 px-4">
          <Card variant="legacy" className="mb-6">
            <div className="rounded-t mb-0 px-6 py-6">
              <div className="text-center mb-3">
                <h6 className="text-slate-500 text-sm font-bold">
                  Iniciar Sesión
                </h6>
              </div>
              <hr className="mt-6 border-b-1 border-slate-300" />
            </div>
            <div className="flex-auto px-4 lg:px-10 py-10 pt-0">
              <form onSubmit={handleSubmit}>
                <div className="relative w-full mb-3">
                  <label
                    className="block uppercase text-slate-600 text-xs font-bold mb-2"
                    htmlFor="username"
                  >
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    className="border-0 px-3 py-3 placeholder-slate-300 text-slate-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                    placeholder="Username"
                    id='username'
                    ref={userRef}
                    autoComplete='off'
                    {...userAttribs}
                  />
                </div>

                <div className="relative w-full mb-3">
                  <label
                    className="block uppercase text-slate-600 text-xs font-bold mb-2"
                    htmlFor="password"
                  >
                    Contraseña
                  </label>
                  <input
                    id='password'
                    type="password"
                    className="border-0 px-3 py-3 placeholder-slate-300 text-slate-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                    placeholder="Password"
                    onChange={(e) => setPassword(e.target.value)}
                    value={password}
                    required
                  />
                </div>
                <div>
                  <label
                    className="inline-flex items-center cursor-pointer"
                    htmlFor='persist'>
                    <input
                      type="checkbox"
                      className="form-checkbox border-0 rounded text-slate-700 ml-1 w-5 h-5 ease-linear transition-all duration-150"
                      id='persist'
                      onChange={toggleCheck}
                      checked={check}
                    />
                    <span className="ml-2 text-sm font-semibold text-slate-600">
                      Recordar mis datos
                    </span>
                  </label>
                </div>

                <div className="text-center mt-6">
                  <button
                    className="bg-slate-800 text-white active:bg-slate-600 text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 w-full ease-linear transition-all duration-150"
                    type='submit'
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Iniciando...' : 'Iniciar'}
                  </button>

                  <p ref={errRef} className={errMsg ? 'errmsg' : 'offscreen'} aria-live='assertive'>{errMsg}</p>
                </div>
              </form>
            </div>
          </Card>
          <div className="flex flex-wrap mt-6 relative">
            <div className="w-1/2">
              <Link
                to="/auth/forgot-password"
                className="text-slate-200"
              >
                <small>Olvidaste tu contraseña?</small>
              </Link>
            </div>
            <div className="w-1/2 text-right">
              <Link to="/auth/register" className="text-slate-200">
                <small>Crear nueva cuenta</small>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Max Sessions Dialog */}
      <AlertDialog open={showMaxSessions} onOpenChange={setShowMaxSessions}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Advertencia</AlertDialogTitle>
            <AlertDialogDescription>
              Has alcanzado el número máximo de sesiones activas
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => loginWithSessionMode('closeOldest')}
              className="bg-blue-500 hover:bg-blue-600"
            >
              Cerrar sesión más antigua
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => loginWithSessionMode('closeAll')}
              className="bg-red-500 hover:bg-red-600"
            >
              Cerrar todas las sesiones
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Login;
